/**
 * MarketingCalendar — W48 Marketing Dashboard Component (B)
 *
 * 12-month or quarter-toggle grid showing marketing_events as colored chips
 * by type. Click chip to navigate to event detail route.
 *
 * No Recharts. Custom SVG / HTML calendar grid.
 * Canon: 5-color palette + teal #0F766E for event type chips.
 */

import { useEffect, useState, useCallback } from "react";
import { useLocation } from "wouter";
import { getSession } from "@/lib/session";

type RangeMode = "q" | "y";

interface CalendarEvent {
  id: string;
  name: string;
  event_date: string | null;
  description: string | null;
  type: string;
  status: string;
  campaign_theme: string | null;
}

interface CalendarData {
  events: CalendarEvent[];
  range: RangeMode;
  bounds: { start: string; end: string };
}

// Event type color mapping — uses canon palette
const TYPE_COLORS: Record<string, { bg: string; text: string; label: string }> = {
  webinar:       { bg: "#EAB308", text: "#713F12", label: "Webinar" },
  conference:    { bg: "#EA580C", text: "#FFFFFF", label: "Conference" },
  roundtable:    { bg: "#16A34A", text: "#FFFFFF", label: "Round Table" },
  round_table:   { bg: "#16A34A", text: "#FFFFFF", label: "Round Table" },
  linkedin:      { bg: "#0F766E", text: "#FFFFFF", label: "LinkedIn" },
  email:         { bg: "#0B0B0F", text: "#FFFFFF", label: "Email" },
  content:       { bg: "#7C3AED", text: "#FFFFFF", label: "Content" },
  partner:       { bg: "#7F1D1D", text: "#FFFFFF", label: "Partner" },
  event:         { bg: "#6B7280", text: "#FFFFFF", label: "Event" },
  default:       { bg: "#6B7280", text: "#FFFFFF", label: "Event" },
};

function getTypeStyle(type: string) {
  const key = (type ?? "").toLowerCase().replace(/\s+/g, "_");
  return TYPE_COLORS[key] ?? TYPE_COLORS.default;
}

function formatMonthYear(dateStr: string): string {
  const d = new Date(dateStr + "T12:00:00");
  return d.toLocaleDateString("en-US", { month: "short", year: "numeric" });
}

function getDayLabel(dateStr: string): string {
  const d = new Date(dateStr + "T12:00:00");
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

function groupByMonth(events: CalendarEvent[]): Map<string, CalendarEvent[]> {
  const map = new Map<string, CalendarEvent[]>();
  for (const e of events) {
    if (!e.event_date) {
      const key = "Undated";
      if (!map.has(key)) map.set(key, []);
      map.get(key)!.push(e);
      continue;
    }
    const d = new Date(e.event_date + "T12:00:00");
    const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
    if (!map.has(key)) map.set(key, []);
    map.get(key)!.push(e);
  }
  // Sort keys chronologically
  return new Map([...map.entries()].sort((a, b) => a[0].localeCompare(b[0])));
}

function Skeleton() {
  return (
    <div className="animate-pulse space-y-3">
      {[1, 2, 3].map((i) => (
        <div key={i} className="h-16 rounded bg-[hsl(var(--surface-2))]" />
      ))}
    </div>
  );
}

export function MarketingCalendar() {
  const session = getSession();
  const [, navigate] = useLocation();
  const [range, setRange] = useState<RangeMode>("q");
  const [data, setData] = useState<CalendarData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback((r: RangeMode) => {
    const token = session?.token;
    if (!token) { setError("Not authenticated"); setLoading(false); return; }
    setLoading(true);
    fetch(`/api/dashboards/marketing/calendar?range=${r}`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((res) => { if (!res.ok) throw new Error(`${res.status}`); return res.json(); })
      .then((d) => { setData(d); setLoading(false); })
      .catch((e) => {
        console.error("[MarketingCalendar]", e);
        setError("Could not load calendar data");
        setLoading(false);
      });
  }, [session?.token]);

  useEffect(() => { load(range); }, [range, load]);

  const monthGroups = data ? groupByMonth(data.events) : new Map();

  return (
    <div className="flex flex-col gap-4">
      {/* Range toggle */}
      <div className="flex items-center gap-2">
        {(["q", "y"] as RangeMode[]).map((r) => (
          <button
            key={r}
            type="button"
            onClick={() => setRange(r)}
            className={`px-3 py-1 text-[12px] font-medium rounded transition-colors ${
              range === r
                ? "bg-[#0F766E] text-white"
                : "bg-[hsl(var(--surface-2))] text-muted-foreground hover:text-foreground"
            }`}
          >
            {r === "q" ? "This quarter" : "Full year"}
          </button>
        ))}
      </div>

      {error && <p className="text-[12px] text-[#DC2626]">{error}</p>}

      {loading ? (
        <Skeleton />
      ) : data && data.events.length === 0 ? (
        <div className="rounded-lg border border-border bg-[hsl(var(--surface))] p-6 text-center">
          <p className="text-[13px] text-muted-foreground">
            No marketing events found for{" "}
            {range === "q" ? "the current quarter" : "this year"}.
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {[...monthGroups.entries()].map(([monthKey, events]) => (
            <div key={monthKey}>
              {/* Month header */}
              <div className="mb-2 flex items-center gap-2">
                <span className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                  {monthKey === "Undated"
                    ? "Undated"
                    : formatMonthYear(monthKey + "-01")}
                </span>
                <span className="text-[11px] text-muted-foreground">
                  ({events.length} event{events.length !== 1 ? "s" : ""})
                </span>
              </div>

              {/* Event chips grid */}
              <div className="flex flex-wrap gap-2">
                {events.map((ev) => {
                  const style = getTypeStyle(ev.type);
                  return (
                    <button
                      key={ev.id}
                      type="button"
                      onClick={() => navigate(`/log/marketing/event/${ev.id}`)}
                      title={ev.description ?? ev.name}
                      className="flex flex-col items-start rounded px-2.5 py-1.5 text-left transition-opacity hover:opacity-80 focus:outline-none focus:ring-2 focus:ring-[#0F766E]"
                      style={{ backgroundColor: style.bg }}
                    >
                      <span
                        className="text-[11px] font-semibold leading-tight"
                        style={{ color: style.text }}
                      >
                        {ev.name.length > 28 ? `${ev.name.slice(0, 25)}…` : ev.name}
                      </span>
                      <span
                        className="mt-0.5 text-[10px] font-medium"
                        style={{ color: style.text, opacity: 0.8 }}
                      >
                        {ev.event_date ? getDayLabel(ev.event_date) : "TBD"} · {style.label}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Event type legend */}
      <div className="flex flex-wrap gap-x-3 gap-y-1 pt-1">
        {Object.entries(TYPE_COLORS)
          .filter(([k]) => k !== "default" && k !== "round_table")
          .map(([k, v]) => (
            <div key={k} className="flex items-center gap-1 text-[10px]">
              <span
                className="inline-block w-2.5 h-2.5 rounded-sm flex-shrink-0"
                style={{ backgroundColor: v.bg }}
              />
              <span className="text-muted-foreground">{v.label}</span>
            </div>
          ))}
      </div>
    </div>
  );
}
